import { useCallback, useEffect, useRef, useState } from 'react';
import { config } from '@/config';

const API = config.BACKEND_URL;

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function useGameTimer({ instanceId, token }) {
  const [elapsedMs, setElapsedMs] = useState(0);
  const [running, setRunning] = useState(false);
  const [payload, setPayload] = useState(null);
  const [kind, setKind] = useState(null);
  const [level, setLevel] = useState(null);
  const [error, setError] = useState(null);
  const [alreadyWon, setAlreadyWon] = useState(false);
  const [progress, setProgress] = useState({});
  const baseRef = useRef(0);
  const startedAtRef = useRef(null);
  const tickRef = useRef(null);
  const completingRef = useRef(false);

  const stopTick = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (startedAtRef.current) {
      baseRef.current += Date.now() - startedAtRef.current;
      startedAtRef.current = null;
      setElapsedMs(baseRef.current);
    }
    setRunning(false);
  }, []);

  const startTick = useCallback((baseMs = 0) => {
    baseRef.current = baseMs;
    startedAtRef.current = Date.now();
    setElapsedMs(baseMs);
    setRunning(true);
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setElapsedMs(baseRef.current + (Date.now() - (startedAtRef.current || Date.now())));
    }, 200);
  }, []);

  const pause = useCallback(async () => {
    if (!instanceId || !token || completingRef.current) return;
    stopTick();
    try {
      const res = await fetch(`${API}/group/games/${instanceId}/pause`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      if (res.ok) {
        const data = await res.json();
        if (typeof data.elapsed_ms === 'number') {
          baseRef.current = data.elapsed_ms;
          setElapsedMs(data.elapsed_ms);
        }
      }
    } catch {
      /* ignore network pause errors */
    }
  }, [instanceId, token, stopTick]);

  const start = useCallback(async () => {
    if (!instanceId || !token) return null;
    setError(null);
    const res = await fetch(`${API}/group/games/${instanceId}/start`, {
      method: 'POST',
      headers: authHeaders(token),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (res.status === 400 && /déjà réussie/i.test(data.detail || '')) {
        setAlreadyWon(true);
        return { alreadyWon: true };
      }
      setError(data.detail || 'Impossible de démarrer la partie');
      return null;
    }
    setPayload(data.payload || null);
    setKind(data.kind);
    setLevel(data.level);
    setProgress(data.progress || {});
    startTick(Number(data.elapsed_ms) || 0);
    return data;
  }, [instanceId, token, startTick]);

  const complete = useCallback(async () => {
    if (!instanceId || !token || completingRef.current) return null;
    completingRef.current = true;
    stopTick();
    try {
      const res = await fetch(`${API}/group/games/${instanceId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Validation impossible');
        completingRef.current = false;
        return null;
      }
      setAlreadyWon(true);
      if (typeof data.elapsed_ms === 'number') setElapsedMs(data.elapsed_ms);
      return data;
    } catch {
      completingRef.current = false;
      return null;
    }
  }, [instanceId, token, stopTick]);

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') pause();
    };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      pause();
    };
  }, [pause]);

  const saveProgress = useCallback(async (nextProgress) => {
    if (!instanceId || !token || completingRef.current) return;
    setProgress(nextProgress || {});
    try {
      await fetch(`${API}/group/games/${instanceId}/progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
        body: JSON.stringify({ progress: nextProgress || {} }),
      });
    } catch {
      /* ignore */
    }
  }, [instanceId, token]);

  return { elapsedMs, running, payload, kind, level, error, alreadyWon, progress, start, pause, complete, saveProgress, startTick };
}
